import React, { useState, useEffect } from 'react';
import { fetchAPI } from '../../../services/apiClient';

export const LocalConfigTicketPage: React.FC = () => {
  const [nombreTicket, setNombreTicket] = useState('');
  const [notasTicket, setNotasTicket] = useState('');
  const [preview, setPreview] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    cargarConfig();
  }, []);

  const cargarConfig = async () => {
    try {
      const data = await fetchAPI<{ nombre_ticket: string | null; notas_ticket: string | null }>(
        '/operaciones/local/ticket-config'
      );
      setNombreTicket(data.nombre_ticket || '');
      setNotasTicket(data.notas_ticket || '');
    } catch (error: any) {
      console.error('Error cargando configuración:', error);
    }
  };

  const guardarConfig = async () => {
    setLoading(true);
    setMensaje('');
    try {
      await fetchAPI('/operaciones/local/ticket-config', {
        method: 'PUT',
        body: JSON.stringify({
          nombre_ticket: nombreTicket.trim() || null,
          notas_ticket: notasTicket.trim() || null,
        }),
      });
      setMensaje('Configuración guardada correctamente');
      setTimeout(() => setMensaje(''), 3000);
    } catch (error: any) {
      setMensaje(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const generarPreview = () => {
    const lineas: string[] = [];
    const nombre = nombreTicket.trim() || '[NOMBRE DEL LOCAL]';
    lineas.push(centrarTexto(nombre, 32));
    lineas.push('-'.repeat(32));
    lineas.push('SORTEO: TICA 12:00 PM');
    lineas.push('CIERRE: 11:30 AM');
    lineas.push('-'.repeat(32));
    lineas.push('NUMERO   MONTO');
    lineas.push('25       ₡500');
    lineas.push('40       ₡1000');
    lineas.push('-'.repeat(32));
    lineas.push('TOTAL: ₡1500');
    lineas.push('METODO: EFECTIVO');
    lineas.push('FECHA: 28/02/2026 11:05 AM');
    lineas.push('TICKET: #A8F2Q');
    lineas.push('-'.repeat(32));
    if (notasTicket.trim()) {
      lineas.push('NOTA DEL LOCAL:');
      const palabras = notasTicket.trim().split(' ');
      let lineaActual = '';
      for (const palabra of palabras) {
        if (lineaActual.length + palabra.length + 1 <= 32) {
          if (lineaActual) lineaActual += ' ';
          lineaActual += palabra;
        } else {
          if (lineaActual) lineas.push(lineaActual);
          lineaActual = palabra;
        }
      }
      if (lineaActual) lineas.push(lineaActual);
    }
    lineas.push('');
    setPreview(lineas.join('\n'));
  };

  const centrarTexto = (texto: string, ancho: number): string => {
    if (texto.length >= ancho) return texto.substring(0, ancho);
    const espacios = Math.floor((ancho - texto.length) / 2);
    return ' '.repeat(espacios) + texto;
  };

  useEffect(() => {
    generarPreview();
  }, [nombreTicket, notasTicket]);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Configuración de Ticket</h1>

      <div className="bg-white rounded-lg shadow p-6 max-w-4xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Formulario */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Configuración</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre a imprimir en ticket
              </label>
              <input
                type="text"
                value={nombreTicket}
                onChange={(e) => setNombreTicket(e.target.value)}
                placeholder="Dejar vacío para usar nombre del local"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                maxLength={200}
              />
              <p className="text-xs text-gray-500 mt-1">
                Este nombre aparecerá en la parte superior del ticket
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notas impresas al final del ticket
              </label>
              <textarea
                value={notasTicket}
                onChange={(e) => setNotasTicket(e.target.value)}
                placeholder="Ej: Revise su ticket antes de retirarse"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={3}
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">
                Mensaje personalizado que aparecerá al final del ticket
              </p>
            </div>

            <button
              onClick={guardarConfig}
              disabled={loading}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Guardando...' : 'Guardar Configuración'}
            </button>

            {mensaje && (
              <div className={`mt-4 p-3 rounded-md ${
                mensaje.includes('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
              }`}>
                {mensaje}
              </div>
            )}
          </div>

          {/* Preview */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Vista previa</h2>
            <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-4">
              <pre className="text-xs font-mono whitespace-pre leading-tight">
                {preview}
              </pre>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Ancho del ticket: 80mm (aproximadamente 32 caracteres)
            </p>
          </div>
        </div>

        {/* Reglas */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Reglas importantes:</h3>
          <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
            <li>El nombre de la BANCA NO se imprime en el ticket</li>
            <li>El nombre del local es configurable (usa el nombre real si se deja vacío)</li>
            <li>Las notas son opcionales pero recomendadas</li>
            <li>Los tickets se imprimen automáticamente al confirmar venta</li>
            <li>Puedes reimprimir tickets desde "Mis Ventas"</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default LocalConfigTicketPage;
